/* Which teams are expected to deliver an ask (designs/intake-ownership-
   model.md OM1/OM2) — shared by the capture form and the edit form so the
   tri-state lives in ONE place and both surfaces cannot drift.

   The control carries three honest answers, not two:

     null  →  NOT RECORDED. Nobody was asked. The resting state, and what
              every ask stored before this question existed reads back as.
     []    →  NOT YET DECIDED. Ticked deliberately: they discussed it and
              have not settled. A different fact from "nobody asked".
     [ids] →  recorded — one team, or several, because ownership is spread
              at least as often as it is singular.

   Un-ticking the last team returns to null rather than [], because removing
   your only answer is not the same as declaring the question open. Saying
   "not settled" takes its own deliberate tick. */
import type { JSX } from "react";
import { TEAM_GROUPS, type CanonicalTeam } from "../../../shared/canonical-teams";

export function OwnersField({
  teams,
  value,
  onChange,
}: {
  teams: CanonicalTeam[];
  value: string[] | null;
  onChange: (next: string[] | null) => void;
}): JSX.Element {
  const picked = value ?? [];
  const undecided = value !== null && value.length === 0;
  const live = teams.filter((t) => !t.retired);
  /* platform teams first: they are the likeliest owners and the shortest
     list, so the reader is not hunting past thirteen vehicles to find them */
  const groups: { label: string; teams: CanonicalTeam[] }[] = TEAM_GROUPS.map((g) => ({
    label: g.label,
    teams: live.filter((t) => t.kind === g.kind),
  })).filter((g) => g.teams.length > 0);

  const toggleTeam = (id: string): void => {
    const next = picked.includes(id) ? picked.filter((v) => v !== id) : [...picked, id];
    /* dropping the last named team means "not recorded" again, not "open" */
    onChange(next.length === 0 ? null : next);
  };

  return (
    <div className="capture-field capture-wide" data-owners-field>
      <span>Which teams are expected to deliver this?</span>
      {groups.map((g) => (
        <div key={g.label} className="owners-group">
          <span className="owners-group-label">{g.label}</span>
          <div className="capture-checks">
            {g.teams.map((t) => (
              <label
                key={t.id}
                className={picked.includes(t.id) ? "capture-check on" : "capture-check"}
              >
                <input
                  type="checkbox"
                  checked={picked.includes(t.id)}
                  onChange={() => toggleTeam(t.id)}
                />
                {t.name}
              </label>
            ))}
          </div>
        </div>
      ))}
      <label className={undecided ? "capture-check on" : "capture-check"}>
        <input
          type="checkbox"
          checked={undecided}
          onChange={() => onChange(undecided ? null : [])}
        />
        Discussed — not yet decided
      </label>
      <p className="owners-note">
        {value === null
          ? "Not recorded — nobody has been asked yet."
          : undecided
            ? "Recorded as an open question."
            : `${picked.length} team${picked.length === 1 ? "" : "s"} expected to deliver this.`}
      </p>
    </div>
  );
}
